import * as React from "react";
import { Alert, Button, Card, Descriptions, InputNumber, Result, Select, Space, Typography } from "antd";
import { ArrowLeftOutlined, InboxOutlined, PrinterOutlined, RollbackOutlined, SendOutlined } from "@ant-design/icons";
import { ApiConfig } from "../utils/AuthorizedApiBase";
import {
  ReturnedShipmentDto,
  ReturnedShipmentResendResponseDto,
  ReturnedShipmentsClient
} from "../utils/scanner-backend";
import { getApiErrorMessage } from "../utils/apiError";

const { Text, Title } = Typography;

const returnedShipmentsClient = new ReturnedShipmentsClient(new ApiConfig());

const formatMoney = (amount: number, currencyCode: string) =>
  new Intl.NumberFormat("sk-SK", { style: "currency", currency: currencyCode }).format(amount);

const formatDateTime = (value: string) =>
  new Intl.DateTimeFormat("sk-SK", {
    day: "numeric",
    month: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(new Date(value));

const carrierStatus = (returned: ReturnedShipmentDto) =>
  returned.CarrierStatusText ? ` – „${returned.CarrierStatusText}“` : "";

/** The order's newest label the courier took over and did not deliver to the customer (S-507). */
export function ReturnedParcelAlert({ returned }: { returned: ReturnedShipmentDto }) {
  const label = `${returned.ShippingCompanyDisplayName} ${returned.TrackingCode}`;
  if (returned.ReturnedAt === null) {
    return (
      <Alert
        type="warning"
        showIcon
        message={`Zásielka ${label} nebola doručená`}
        description={`Kuriér zatiaľ nenahlásil, že ju vracia${carrierStatus(returned)}. Pošlite ju znova, len ak ju vrátil kuriér a balík máte v ruke. Ak tovar vracia zákazník, zvoľte Vrátiť tovar na sklad.`}
      />
    );
  }

  return (
    <Alert
      type="warning"
      showIcon
      icon={<RollbackOutlined />}
      message={`Zásielka ${label} sa vrátila nedoručená`}
      description={`Kuriér ju vrátil ${formatDateTime(returned.ReturnedAt)}${carrierStatus(returned)}.`}
    />
  );
}

type DecisionProps = {
  orderCode: string;
  companyName: string;
  returned: ReturnedShipmentDto;
  onResend: () => void;
  onRestock: () => void;
  onBack: () => void;
};

const bigButtonStyle: React.CSSProperties = { height: 64, fontSize: 17, textAlign: "left", paddingLeft: 16 };

export function ReturnedParcelDecision({ orderCode, companyName, returned, onResend, onRestock, onBack }: DecisionProps) {
  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Card>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <ReturnedParcelAlert returned={returned} />
          <Descriptions column={1} size="small" title="Objednávka">
            <Descriptions.Item label="Objednávka">#{orderCode}</Descriptions.Item>
            <Descriptions.Item label="Zákazník">{companyName}</Descriptions.Item>
            {returned.CashOnDeliveryAmount > 0 ? (
              <Descriptions.Item label="Dobierka">
                {formatMoney(returned.CashOnDeliveryAmount, returned.CurrencyCode)}
              </Descriptions.Item>
            ) : null}
          </Descriptions>
        </Space>
      </Card>
      <Title level={5} style={{ margin: 0 }}>
        Čo s balíkom?
      </Title>
      <Button type="primary" block icon={<SendOutlined />} style={bigButtonStyle} onClick={onResend}>
        Poslať znova zákazníkovi
      </Button>
      <Text type="secondary" style={{ marginTop: -8 }}>
        Balík bude znovu odoslaný zákazníkovi s rovnakým obsahom, bez príjemky a bez pohybu na sklade. V ďalšom kroku
        vytlačíte nový štítok.
      </Text>
      <Button block icon={<InboxOutlined />} style={bigButtonStyle} onClick={onRestock}>
        Vrátiť tovar na sklad
      </Button>
      <Text type="secondary" style={{ marginTop: -8 }}>
        Príjem od zákazníka: príjemka a tovar na pozície.
      </Text>
      <Button block icon={<ArrowLeftOutlined />} onClick={onBack}>
        Späť
      </Button>
    </Space>
  );
}

type LabelFormProps = {
  orderCode: string;
  companyName: string;
  returned: ReturnedShipmentDto;
  onBooked: (result: ReturnedShipmentResendResponseDto) => void;
  onBack: () => void;
};

export function ReturnedParcelLabelForm({
  orderCode,
  companyName,
  returned,
  onBooked,
  onBack
}: LabelFormProps) {
  const [carrier, setCarrier] = React.useState(returned.Carriers[0].Code);
  const [packageCount, setPackageCount] = React.useState<number | null>(returned.PackageCount);
  const [weightKg, setWeightKg] = React.useState<number | null>(returned.TotalWeightKg);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const canSubmit = packageCount !== null && packageCount >= 1 && weightKg !== null && weightKg > 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await returnedShipmentsClient.resend(Number(orderCode), {
        ShippingCompany: carrier,
        PackageCount: packageCount,
        TotalWeightKg: weightKg
      });
      onBooked(result);
    } catch (submitError) {
      setError(getApiErrorMessage(submitError));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Text type="secondary">
        Objednávka #{orderCode} · {companyName} · zásielka {returned.ShippingCompanyDisplayName} {returned.TrackingCode}
      </Text>
      {returned.ReturnedAt === null ? <ReturnedParcelAlert returned={returned} /> : null}
      <Card title="Nový štítok">
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Space direction="vertical" size={2} style={{ width: "100%" }}>
            <Text type="secondary">Prepravca</Text>
            <Select value={carrier} onChange={setCarrier} style={{ width: "100%" }} size="large">
              {returned.Carriers.map(option => (
                <Select.Option key={option.Code} value={option.Code}>
                  {option.Name}
                </Select.Option>
              ))}
            </Select>
          </Space>
          <Space size="middle" style={{ width: "100%" }}>
            <Space direction="vertical" size={2}>
              <Text type="secondary">Počet balíkov</Text>
              <InputNumber
                aria-label="Počet balíkov"
                min={1}
                precision={0}
                value={packageCount}
                onChange={setPackageCount}
                size="large"
                style={{ width: 120 }}
              />
            </Space>
            <Space direction="vertical" size={2}>
              <Text type="secondary">Váha (kg)</Text>
              <InputNumber
                aria-label="Váha (kg)"
                min={0}
                step={0.1}
                value={weightKg}
                onChange={setWeightKg}
                size="large"
                style={{ width: 120 }}
              />
            </Space>
          </Space>
          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label="Dobierka">
              {returned.CashOnDeliveryAmount > 0 ? (
                <>
                  <Text strong>{formatMoney(returned.CashOnDeliveryAmount, returned.CurrencyCode)}</Text>{" "}
                  <Text type="secondary">(zostáva zaplatiť)</Text>
                </>
              ) : (
                "bez dobierky"
              )}
            </Descriptions.Item>
          </Descriptions>
          <Text type="secondary">
            Bez príjemky a bez pohybu na sklade. Zákazník dostane e-mail s novým číslom balíka.
          </Text>
          {error ? <Alert type="error" showIcon message={error} /> : null}
          <Space direction="vertical" style={{ width: "100%" }}>
            <Button
              type="primary"
              size="large"
              block
              icon={<PrinterOutlined />}
              loading={submitting}
              disabled={!canSubmit}
              onClick={() => void handleSubmit()}
            >
              Vytlačiť nový štítok
            </Button>
            <Button block icon={<ArrowLeftOutlined />} onClick={onBack} disabled={submitting}>
              Späť
            </Button>
          </Space>
        </Space>
      </Card>
    </Space>
  );
}

type ResultProps = {
  orderCode: string;
  result: ReturnedShipmentResendResponseDto;
  onNew: () => void;
};

export function ReturnedParcelResult({ orderCode, result, onNew }: ResultProps) {
  const [printError, setPrintError] = React.useState(result.PrintError);
  const [reprinting, setReprinting] = React.useState(false);

  const handleReprint = async () => {
    setReprinting(true);
    try {
      const reprint = await returnedShipmentsClient.reprint(result.ShipmentId);
      setPrintError(reprint.PrintError);
    } catch (reprintError) {
      setPrintError(getApiErrorMessage(reprintError));
    } finally {
      setReprinting(false);
    }
  };

  const label = `${result.ShippingCompanyDisplayName} ${result.TrackingCode}`;
  const nextSteps = (
    <Space key="info" direction="vertical" size={4} style={{ textAlign: "left", width: "100%" }}>
      <Text>Objednávka #{orderCode} ostáva odoslaná, v jej zásielkach pribudla nová.</Text>
      <Text type="secondary">Zákazník dostane e-mail s novým číslom balíka.</Text>
    </Space>
  );

  if (printError) {
    return (
      <Result
        status="warning"
        title="Preprava je objednaná, štítok sa nevytlačil"
        subTitle={label}
        extra={[
          <Alert key="error" type="error" showIcon message={printError} style={{ textAlign: "left" }} />,
          nextSteps,
          <Space key="actions" style={{ marginTop: 16 }}>
            <Button type="primary" icon={<PrinterOutlined />} loading={reprinting} onClick={() => void handleReprint()}>
              Vytlačiť znova
            </Button>
            <Button onClick={onNew}>Ďalší balík</Button>
          </Space>
        ]}
      />
    );
  }

  return (
    <Result
      status="success"
      title="Nový štítok je vytlačený"
      subTitle={label}
      extra={[
        nextSteps,
        <Button key="new" type="primary" onClick={onNew} style={{ marginTop: 16 }}>
          Ďalší balík
        </Button>
      ]}
    />
  );
}
